import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2, KeyRound } from "lucide-react";
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { apiClient } from '@rti/shared';
import TermsConditions from '../components/TermsConditions';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import { decodePublicKeyCredentialRequestOptions, serializeAssertion } from '@/utils/webauthn';

// Declare grecaptcha to keep TS happy when loaded dynamically
declare global {
  interface Window { grecaptcha?: any }
}

const Authorization: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [needsPasswordUpdate, setNeedsPasswordUpdate] = useState(false);
  const [isTokenResetMode, setIsTokenResetMode] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    newPassword: '',
    confirmPassword: '',
    userId: null as number | null
  });

  const [resetToken, setResetToken] = useState<string | null>(null);
  const [resetEmail, setResetEmail] = useState<string | null>(null);

  // Forgot password flow state
  const [showResetRequest, setShowResetRequest] = useState(false);
  const [resetRequestEmail, setResetRequestEmail] = useState('');
  const [resetRequestSubmitted, setResetRequestSubmitted] = useState(false);
  const [resetRequestError, setResetRequestError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(60);
  const showForgotPW = true;
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, isAuthenticated } = useAuth();
  const { isDark } = useTheme();

  const recaptchaSiteKey = (import.meta as any).env?.VITE_RECAPTCHA_SITE_KEY as string | undefined;
  const enablePasskey = ((import.meta as any).env?.VITE_ENABLE_PASSKEY as string | undefined) === 'true';

  useEffect(() => {
    setFormData({
      username: '',
      password: '',
      newPassword: '',
      confirmPassword: '',
      userId: null
    });
    setError('');
    setLoading(false);
    setNeedsPasswordUpdate(false);
    setShowPassword(false);
    setShowNewPassword(false);
    setFormKey(prev => prev + 1);
  }, []);

  // Redirect authenticated users away from authorization page
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const from = (location.state as any)?.from || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, location.state]);

  // Show the reCAPTCHA badge only while on the Authorization page
  useEffect(() => {
    document.body.classList.add('show-recaptcha-badge');
    return () => {
      document.body.classList.remove('show-recaptcha-badge');
      try {
        document.querySelectorAll('.grecaptcha-badge').forEach((el) => el.remove());
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (!recaptchaSiteKey) return;

    const existingScript = document.querySelector(`script[src*="recaptcha"]`);
    if (existingScript || window.grecaptcha) return;

    const script = document.createElement('script');
    script.src = `https://www.recaptcha.net/recaptcha/api.js?render=${recaptchaSiteKey}`;
    script.async = true;
    script.defer = true;

    const cleanup = () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };

    document.head.appendChild(script);
    return cleanup;
  }, [recaptchaSiteKey]);

  // Countdown timer for resend password reset email
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const getRecaptchaToken = async (): Promise<string | undefined> => {
    if (!recaptchaSiteKey) return undefined;

    // Wait for grecaptcha to be available (in case user submits before script finishes loading)
    const waitForGrecaptcha = async (timeoutMs = 5000): Promise<any> => {
      const start = Date.now();
      while (!window.grecaptcha) {
        if (Date.now() - start > timeoutMs) return undefined;
        await new Promise((r) => setTimeout(r, 100));
      }
      return window.grecaptcha;
    };

    try {
      const grecaptcha = await waitForGrecaptcha();
      if (!grecaptcha) return undefined;

      // grecaptcha.ready(cb) is callback-based; wrap it so we can await
      await new Promise<void>((resolve) => {
        try {
          grecaptcha.ready(() => resolve());
        } catch {
          // Some implementations may not require ready; resolve immediately
          resolve();
        }
      });

      const token = await grecaptcha.execute(recaptchaSiteKey, { action: 'login' });
      return typeof token === 'string' && token.length > 0 ? token : undefined;
    } catch {
      return undefined;
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const recaptchaToken = await getRecaptchaToken();
      const payload: any = {
        user: {
          username: formData.username,
          password: formData.password,
        },
        recaptchaToken,
      };
      const user = await login(payload);

      if (user.needs_password_update) {
        navigate('/account-settings', { state: { requirePasswordChange: true, currentPassword: formData.password } });
      } else {
        // Redirect to the page the user was trying to access, or dashboard
        const from = (location.state as any)?.from || '/dashboard';
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      // Prefer backend-provided detail message when present
      const detail =
        err?.response?.data?.detail ??
        err?.data?.detail ??
        err?.detail ??
        (typeof err?.message === 'string' ? err.message : undefined);
      const msg = typeof detail === 'string'
        ? detail
        : (detail && typeof detail === 'object' && typeof detail.message === 'string')
          ? detail.message
          : 'Login failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setError('');
    try {
      if (!('PublicKeyCredential' in window)) {
        setError('Passkeys are not supported in this browser.');
        return;
      }
      if (!enablePasskey) {
        setError('Passkey sign-in is not enabled.');
        return;
      }
      if (!formData.username) {
        setError('Enter your username to continue with Passkey.');
        return;
      }
      const optionsJson = await apiClient.beginPasskeyLogin({ username: formData.username }).catch((e) => {
        throw new Error(e?.response?.data?.detail || 'Passkey not available.');
      });
      const publicKey: PublicKeyCredentialRequestOptions = decodePublicKeyCredentialRequestOptions(optionsJson) as any;
      const assertion: any = await (navigator as any).credentials.get({ publicKey });
      const serialized = serializeAssertion(assertion);
      const loginResp = await apiClient.finishPasskeyLogin({ username: formData.username, credential: serialized }).catch((e) => {
        throw new Error(e?.response?.data?.detail || 'Passkey authentication failed.');
      });
      const { token, user } = loginResp as any;
      localStorage.setItem('access_token', token);
      localStorage.setItem('user', JSON.stringify(user));
      navigate('/dashboard');
    } catch (err: any) {
      setError(err?.message || 'Passkey sign-in failed.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user types
  };

  // Detect token/email in query params to enable token-based reset
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const email = params.get('email');
    if (token && email) {
      (async () => {
        try {
          setLoading(true);
          setError('');
          const resp = await (apiClient as any).verifyPasswordReset(token, email);
          if (resp && resp.valid) {
            setIsTokenResetMode(true);
            setResetToken(token);
            setResetEmail(email);
            setNeedsPasswordUpdate(true); // Reuse the reset UI
            setFormData(prev => ({ ...prev, password: '', newPassword: '', confirmPassword: '', userId: resp.user_id ?? null }));
          } else {
            setError('Reset link is invalid or expired.');
          }
        } catch (e: any) {
          setError(e?.message || 'Failed to verify reset link.');
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [location.search]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    // In token mode, current password is not required
    if (!isTokenResetMode && !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    if (!formData.newPassword || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (!isTokenResetMode && !agreedToTerms) {
      setError('You must agree to the terms and conditions');
      return;
    }

    setLoading(true);
    setError('');
    try {
      if (isTokenResetMode) {
        if (!resetToken || !resetEmail) throw new Error('Missing reset token.');
        await (apiClient as any).completePasswordReset(resetToken, resetEmail, formData.newPassword);
      } else {
        if (!formData.userId) {
          setError('User ID not found. Please try logging in again.');
          setLoading(false);
          return;
        }
        await (apiClient as any).updatePassword(formData.password, formData.newPassword, formData.userId);
      }

      // Only record Terms acceptance when terms are shown and explicitly accepted
      if (!isTokenResetMode && agreedToTerms) {
        try { await (apiClient as any).acceptTermsOfUse(); } catch {}
      }

      setNeedsPasswordUpdate(false);
      setFormData({ username: '', password: '', newPassword: '', confirmPassword: '', userId: null });
      setAgreedToTerms(false);
      navigate('/authorization');
    } catch (err: any) {
      setError(err.message || 'Password update failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreedToTerms(e.target.checked);
    if (e.target.checked) {
      setShowTerms(true);
    }
  };

  const handleTermsLinkClick = () => {
    setShowTerms(true);
  };

  const handleResetRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetRequestError('');
    try {
      const email = resetRequestEmail.trim();
      if (!email) {
        setResetRequestError('Please enter your email');
        return;
      }
      await (apiClient as any).requestPasswordReset(email);
      setResetRequestSubmitted(true);
    } catch (err: any) {
      // Always show generic message regardless of backend outcome
      setResetRequestSubmitted(true);
    }
  };

  const handleResendResetRequest = async () => {
    setLoading(true);
    setResetRequestError('');
    try {
      const email = resetRequestEmail.trim();
      if (!email) {
        setResetRequestError('Please enter your email');
        return;
      }
      await (apiClient as any).requestPasswordReset(email);
      setResendCountdown(60);
      setResetRequestError('Reset link sent! Please check your inbox and spam folder.');
    } catch (err: any) {
      setResetRequestError('Failed to resend reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Real-time password match for reset flow (only when user has typed both fields)
  const newPwMismatch = useMemo(() => {
    return (
      (formData.newPassword?.length || 0) > 0 &&
      (formData.confirmPassword?.length || 0) > 0 &&
      formData.newPassword !== formData.confirmPassword
    );
  }, [formData.newPassword, formData.confirmPassword]);

  return (
    <div className="root">
      <div className="auth-root flex w-full h-[100vh]">
        {/* Left Section - Login/Password Reset Forms */}
        <div className="flex flex-col justify-between items-start h-full w-full max-w-lg px-5 py-10 bg-rs-black text-rs-off-white">
          <img
            src={isDark ? "/logos/Primary/White.svg" : "/logos/PrimaryRTi/LimeBlack.svg"}
            alt="Resin Smart Logo"
            onClick={handleLogoClick}
            className="w-sm cursor-pointer"
          />
          {!needsPasswordUpdate ? (
            !showResetRequest ? (
              <form key={formKey} onSubmit={handleLogin} className="lg:ml-16 flex flex-col max-w-md" id="login-form" data-hs-do-not-collect="true">
                <h1 className="mb-4 text-5xl font-light">Welcome back!</h1>
                <span className="text-xl dark:text-rs-off-white">Login to your account</span>

                <input
                  data-testid="username-input"
                  className="p-3 rounded-sm w-full max-w-sm bg-transparent border border-rs-jet text-rs-off-white placeholder-rs-off-white/60 focus:border-rs-green focus:outline-none"
                  type="text"
                  placeholder="Username"
                  name="username"
                  value={formData.username}
                  autoComplete="username"
                  onChange={handleInputChange}
                  disabled={loading}
                />

                <div className="relative mt-4">
                  <input
                    data-testid="password-input"
                    className="p-3 rounded-sm w-full max-w-sm bg-transparent border border-rs-jet text-rs-off-white placeholder-rs-off-white/60 focus:border-rs-green focus:outline-none pr-12"
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={loading}
                    autoComplete="current-password"
                  />
                  <button
                    data-testid="toggle-password-visibility-button"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-rs-off-white/60 hover:text-rs-off-white"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>

                {error && <div data-testid="auth-error-message" className="text-rs-red mt-3 text-sm">{error}</div>}
                <div className="flex items-center gap-3 mt-3">
                  <button
                    data-testid="sign-in-button"
                    className="w-sm green text-lg"
                    type="submit"
                    disabled={loading || isLoading}
                  >
                    {(loading || isLoading) ? (
                      <>
                        <Loader2 className="inline animate-spin mr-2" size={20} />
                        Signing In...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </button>
                  {enablePasskey && (
                    <button
                      data-testid="passkey-button"
                      type="button"
                      onClick={handlePasskeyLogin}
                      disabled={loading || isLoading}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-rs-jet rounded text-sm hover:bg-rs-jet/30"
                      title="Sign in with a passkey"
                    >
                      <KeyRound size={18} /> Passkey
                    </button>
                  )}
                </div>
                <div className="mt-3 text-sm flex flex-col gap-1">
                  { showForgotPW && (<a
                    data-testid="forgot-password-link"
                    type="button"
                    className="text-rs-green hover:text-rs-light-green underline cursor-pointer"
                    onClick={() => { setShowResetRequest(true); setResetRequestSubmitted(false); setResetRequestError(''); }}
                  >
                    Forgot your password?
                  </a>
                    )}
                  <span className="text-rs-off-white/60">
                    Don't have an account?{' '}
                    <a href="/create-account" className="text-rs-green hover:text-rs-light-green underline">Create Account</a>
                  </span>
                </div>
                {recaptchaSiteKey && (
                  <p className="mt-3 text-xs text-rs-off-white/60">
                    This site is protected by reCAPTCHA and the Google
                    <a className="underline ml-1" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
                    {' '}and{' '}
                    <a className="underline" href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms of Service</a>
                    {' '} apply.
                  </p>
                )}
              </form>
            ) : (
              <form onSubmit={handleResetRequestSubmit} className="lg:ml-16 flex flex-col max-w-md">
                <h1 className="mb-4 text-4xl font-light">Reset Password</h1>
                {!resetRequestSubmitted ? (
                  <>
                    <span className="text-xl dark:text-rs-off-white">Enter your email to get a reset link</span>
                    <input
                      data-testid="reset-email-input"
                      className="w-sm p-3 rounded-sm w-full max-w-sm mt-4 bg-transparent border border-rs-jet text-rs-off-white placeholder-rs-off-white/60 focus:border-rs-green focus:outline-none"
                      type="email"
                      placeholder="Email"
                      name="reset_email"
                      value={resetRequestEmail}
                      onChange={(e) => setResetRequestEmail(e.target.value)}
                      disabled={loading}
                      autoComplete="email"
                    />
                    {resetRequestError && <div className="text-rs-red mt-3 text-sm">{resetRequestError}</div>}
                    <div className="flex items-center justify-around gap-2 mt-3">
                      <button
                        data-testid="send-reset-link-button"
                        className="w-sm green text-lg"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="inline animate-spin mr-2" size={20} />
                            Sending...
                          </>
                        ) : (
                          'Send reset link'
                        )}
                      </button>
                      <a
                        data-testid="back-to-sign-in-link"
                        className="w-xs underline cursor-pointer"
                        onClick={() => setShowResetRequest(false)}
                      >Back to sign in
                      </a>
                    </div>
                  </>
                ) : (
                  <div className="max-w-sm">
                    <p className="text-base font-light leading-6 mb-4">
                      If your email exists in our system, you will receive a password reset link shortly. If you do not see it, please check your spam or junk folder.
                    </p>
                    {resetRequestError && (
                      <div className={`text-sm mb-4 ${resetRequestError.includes('sent') ? 'text-rs-green' : 'text-rs-red'}`}>
                        {resetRequestError}
                      </div>
                    )}
                    <button
                      data-testid="send-link-again-button"
                      onClick={handleResendResetRequest}
                      disabled={loading || resendCountdown > 0}
                      className="green hover:text-rs-light-green underline disabled:opacity-50 mb-4"
                    >
                      {loading ? 'Sending...' : resendCountdown > 0 ? `Send link again in ${resendCountdown}s` : 'Send link again'}
                    </button>
                    <a
                      data-testid="back-to-sign-in-from-reset-link"
                      className="mt-4 text-sm underline text-rs-green hover:text-rs-light-green cursor-pointer"
                      onClick={() => setShowResetRequest(false)}
                    >
                      Back to sign in
                    </a>
                  </div>
                )}
              </form>
            )
          ) : (
            <form onSubmit={handlePasswordReset} className="ml-15">
              <span className="text-xl mb-6 block dark:text-rs-off-white">Please Reset Your Password</span>

              {/* Current password field is hidden in token mode */}
              { !isTokenResetMode && (
                <div className="relative mb-4">
                  <input
                    data-testid="current-password-input"
                    className="w-sm p-3 rounded-sm w-full max-w-sm bg-transparent border border-rs-jet text-rs-off-white placeholder-rs-off-white/60 focus:border-rs-green focus:outline-none pr-12"
                    type={showPassword ? "text" : "password"}
                    placeholder="Current Password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={loading}
                    autoComplete="current-password"
                  />
                  <button
                    data-testid="toggle-current-password-visibility-button"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-rs-off-white/60 hover:text-rs-off-white"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              )}

              <div className="relative mb-4">
                <input
                  type="hidden"
                  name="username"
                  value={formData.username}
                />
                <input
                  data-testid="new-password-input"
                  className="w-sm p-3 rounded-sm w-full max-w-sm bg-transparent border border-rs-jet text-rs-off-white placeholder-rs-off-white/60 focus:border-rs-green focus:outline-none pr-12"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="New Password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  disabled={loading}
                  autoComplete="new-password"
                />
                <button
                  data-testid="toggle-new-password-visibility-button"
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-rs-off-white/60 hover:text-rs-off-white"
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <div className="relative mb-1">
                <input
                  data-testid="confirm-password-input"
                  className={`w-sm p-3 rounded-sm w-full max-w-sm bg-transparent border ${newPwMismatch ? 'border-rs-red' : 'border-rs-jet'} text-rs-off-white placeholder-rs-off-white/60 focus:outline-none pr-12 ${newPwMismatch ? '' : 'focus:border-rs-green'}`}
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Confirm New Password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  disabled={loading}
                  aria-invalid={newPwMismatch}
                  autoComplete="new-password"
                />
              </div>
              {newPwMismatch && (
                <div className="text-rs-red -mt-1 mb-2 text-xs">Passwords do not match</div>
              )}

              {/* Password strength meter (shared) */}
              <PasswordStrengthMeter password={formData.newPassword} showLabel showFeedback className="mt-2 max-w-sm" />


              {/* Terms and Conditions */}
              { !isTokenResetMode && (<div className="mt-3 text-rs-off-white flex items-start justify-start">
                <input
                  data-testid="agree-terms-checkbox"
                  type="checkbox"
                  id="agree-terms"
                  checked={agreedToTerms}
                  onChange={handleCheckboxChange}
                  className="mt-1 mr-3"
                />
                <label htmlFor="agree-terms" className="text-sm text-rs-off-white">
                  By clicking this box and using this site, you are agreeing to the ResinSmart.ai{' '}
                  <button
                    data-testid="terms-and-conditions-button"
                    type="button"
                    onClick={handleTermsLinkClick}
                    className="text-rs-green hover:text-rs-light-green underline"
                  >
                    terms and conditions
                  </button>
                </label>
              </div>
              )}

              <button
                data-testid="change-password-button"
                className="w-sm green text-lg mt-3"
                type="submit"
                disabled={
                  loading ||
                  (!isTokenResetMode && !agreedToTerms) ||
                  !formData.newPassword ||
                  !formData.confirmPassword ||
                  formData.newPassword !== formData.confirmPassword
                }
              >
                {loading ? (
                  <>
                    <Loader2 className="inline animate-spin mr-2" size={20} />
                    Changing Password...
                  </>
                ) : (
                  'Change Password'
                )}
              </button>
              {recaptchaSiteKey && (
                <p className="mt-3 text-xs text-rs-off-white/60">
                  This site is protected by reCAPTCHA and the Google
                  <a className="underline ml-1" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
                  {' '}and{' '}
                  <a className="underline" href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms of Service</a>
                  {' '}apply.
                </p>
              )}
            </form>
          )}
          <div className="steps"></div>
        </div>
        {/* Right art section */}
        <div className="art-section flex-1 relative overflow-hidden">
          <img
            className="art-section-dashboard w-full max-w-8xl rounded-xl border-4 border-rs-jet"
            src="/dashboard-landing.png"
            alt="RTi Dashboard Preview"
          />
          <div className="patterns-container absolute inset-0 pointer-events-none">
            <img src="/patterns/union-green.svg" className="w-2xl absolute top-10 left-10" alt="" />
            <img src="/patterns/union-green.svg" className="w-2xl absolute bottom-10 right-10" alt="" />
          </div>
        </div>
      </div>
      <TermsConditions isOpen={showTerms} onClose={() => setShowTerms(false)} />
    </div>
  );
};

export default Authorization;
